import _ from "lodash";
import React, { PureComponent } from "react";
import Accordian from "UIComponents/Accordian";
import I18nHOC from "UIComponents/I18nHOC";
import { getElementValue } from "modules/ElementValueUtils";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";
import classes from "./SubjectBenchmarks.scss";
import SubjectBenchMarkView from "../SubjectBenchMarkView";
import { colors } from "Constants";
import { Checkbox } from "@toddle-edu/ds-web";
import { getStyleStrippedText } from "Utils";
const getElementValueMemoize = _.memoize(getElementValue, params => {
  const { valueKeys, fieldUID, benchmarkSet, skipRemoveSubjectCheck } = params;

  // Create hash for value keys using lodash
  const valueKeysHash = _.join(valueKeys || [], ",");

  // Create hash for benchmark set using rootNodes and nodes with lodash
  const rootNodesHash = _.join(_.get(benchmarkSet, "rootNodes", []), ",");
  const nodesHash = _.join(_.map(_.get(benchmarkSet, "nodes", []), "id"), ",");

  return JSON.stringify(
    `${fieldUID}_${valueKeysHash}_${rootNodesHash}_${nodesHash}_${skipRemoveSubjectCheck}`
  );
});

const styles = {
  nodeLabelStyle: [
    {
      depth: 2,
      style: {
        marginBottom: "8px",
      },
    },

    {
      depth: 3,
      style: {},
    },
  ],
  customCountStyle: {
    fontWeight: "500",
    marginRight: "0px",
    color: colors.textSubtle,
  },
};

class SubjectBenchmarks extends PureComponent {
  state = {
    selectedValues: this.props.selectedValues,
  };

  debouncedOnItemSelected = _.debounce(
    params => this.props.onItemSelected(params),
    100
  );

  componentDidUpdate = prevProps => {
    const { selectedValues } = this.props;
    if (!_.isEqual(prevProps.selectedValues, selectedValues)) {
      this.setState({ selectedValues });
    }
  };

  onCheckboxClicked = selectedValues => {
    const {
      props: { subject, nodes, rootNodes },
    } = this;

    this.setState({ selectedValues }, () => {
      this.debouncedOnItemSelected({
        subject,
        selections: selectedValues,
        nodes,
        rootNodes,
      });
    });
  };

  getExpandedContent = ({ elementValue }) => {
    const {
      props: {
        isReverseCheckbox,
        checkboxItemStyle,
        nodeSpanStyle,
        checkboxContainerStyle,
        searchTerm,
        shouldCheckIsLeafProperty = true,
        allowParentTagging,
        showSearchLabel = false,
      },
      state: { selectedValues },
    } = this;

    return (
      <SubjectBenchMarkView
        value={elementValue}
        startDepth={1}
        mode={"view"}
        isTagging={true}
        updateInputField={this.onCheckboxClicked}
        selectedValues={selectedValues}
        isReverseCheckbox={isReverseCheckbox}
        checkboxItemStyle={checkboxItemStyle}
        nodeSpanStyle={nodeSpanStyle}
        nodeLabelStyle={styles.nodeLabelStyle}
        checkboxContainerStyle={checkboxContainerStyle}
        searchTerm={searchTerm}
        shouldCheckIsLeafProperty={shouldCheckIsLeafProperty}
        showSearchLabel={showSearchLabel}
        allowParentTagging={allowParentTagging}
      />
    );
  };

  render() {
    const {
      props: {
        subject,
        nodes,
        rootNodes,
        showAccordian,
        hideContainerBorder,
        showLabelBorder,
        showCountSeperate,
        customTitleStyle,
        isExpanded,
        fieldUID,
        showCountBadge,
        t,
        curriculumType,
        searchTerm,
        selectAllSecondaryNav,
        parent,
        isSearchNavOptionSelected,
      },
      state: { selectedValues },
    } = this;

    const elementValue = _.compact(
      getElementValueMemoize({
        valueKeys: _.map(nodes, node => node.id),
        fieldUID: fieldUID,
        benchmarkSet: { nodes, rootNodes },
        skipRemoveSubjectCheck: true,
      })
    );

    const numOfSelectedValues = _.get(selectedValues, "length", 0);
    const selectedNumberString =
      numOfSelectedValues > 0 && !showCountSeperate
        ? ` (${numOfSelectedValues})`
        : "";

    const isArchived =
      curriculumType === CURRICULUM_TYPE_PYP
        ? _.get(subject, "value.resolvedSubject.isArchived", false)
        : _.get(subject, "value.isArchived", false);

    const title = (
      <div className={classes.titleText}>
        {`${subject.label}${selectedNumberString}`}
        {isArchived ? (
          <div className={classes.archivedText}>{`(${t(
            "common:archived"
          )})`}</div>
        ) : null}
      </div>
    );

    if (showAccordian)
      return (
        <Accordian
          title={title}
          getExpandedContent={() => this.getExpandedContent({ elementValue })}
          hideContainerBorder={hideContainerBorder}
          showLabelBorder={showLabelBorder}
          count={showCountSeperate ? numOfSelectedValues : 0}
          customTitleStyle={customTitleStyle}
          isExpanded={true}
          showCountBadge={showCountBadge}
          accordianHeaderClass={classes.accordianHeaderClass}
          accordianArrowContainerClass={classes.accordianArrowContainerClass}
          customCountStyle={styles.customCountStyle}
          headerStyle={{ padding: "8px 16px !important" }}
          leftArrowStyle={{ marginRight: "0px !important" }}
        />
      );
    const isChecked =
      isSearchNavOptionSelected({
        uid: _.get(subject, "id"),
        parent,
      }) == "CHECKED";

    if (!_.isEmpty(elementValue))
      return (
        <div className={classes.onSearchSectionContainer}>
          <div
            className={classes.onSearchSectionSubHeader}
            data-test-id="journal-postcreation-goal-searchitem"
          >
            <span
              dangerouslySetInnerHTML={{
                __html: getStyleStrippedText({
                  text: subject.label,
                  type: "SANITIZE_DANGER_HTML_TITLE",
                }),
              }}
              style={{ color: colors.textDefault }}
            />
            <div className={classes.checkBoxContainer} onClick={() => {}}>
              <Checkbox
                isChecked={isChecked}
                onClick={() => {
                  selectAllSecondaryNav(
                    _.get(subject, "id"),
                    !isChecked,
                    parent
                  );
                }}
                testId={"journal-postcreation-goal-searchitem"}
              />
            </div>
          </div>
          <div className={classes.onSearchExpandedContainer}>
            {this.getExpandedContent({ elementValue })}
          </div>
        </div>
      );

    return null;
  }
}

SubjectBenchmarks.defaultProps = {
  isReverseCheckbox: false,
  searchTerm: "",
  hideContainerBorder: false,
  showLabelBorder: true,
  showCountSeperate: false,
  checkboxItemStyle: {},
  customTitleStyle: {},
  nodeSpanStyle: {},
  isExpanded: false,
  showAccordian: true,
  fieldUID: "benchmarks",
  checkboxContainerStyle: {},
  showCountBadge: false,
  allowParentTagging: false,
};

export default I18nHOC({ resource: ["common"] })(SubjectBenchmarks);
